import React, { useState, useCallback, memo, useMemo } from "react";
import { ShareButton, AddButton, LikeButton } from "../AddShareLike";
import { useNavigate } from "react-router-dom";
import { formatImageUrl, formatCreatedAtAgo } from "@/lib/general";
import { motion, AnimatePresence } from "framer-motion";
import LazyLoad from "@tripbuilder/image/LazyLoad";
import useTrip from "@/stores/tripBuilder/useTrip";
import { EyeIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { PaperAirplaneIcon as PaperAirplaneIconSolid } from "@heroicons/react/24/solid";
import { ExploreTrip } from "@/types/social";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import { useRateLimitStore } from "@/stores/rateLimitStore";

interface ExplorePostProps {
  post: ExploreTrip;
  anonymousUser: boolean;
}

const EngagementButton = memo(
  ({
    onClick,
    children,
    count,
    className = "",
  }: {
    onClick: () => void;
    children: React.ReactNode;
    count: number;
    className?: string;
  }) => (
    <button
      className={`group relative flex items-center space-x-0.5 rounded-md py-0.5 transition-colors duration-150 ease-in-out ${className}`}
      onClick={onClick}
    >
      <div className="w-8 h-8 flex items-center justify-center">{children}</div>
      <span className="text-[13px] text-slate-800 font-medium tabular-nums min-w-[1rem] text-center group-hover:text-black">
        {count}
      </span>
    </button>
  )
);

EngagementButton.displayName = "EngagementButton";

const ImageGrid = memo(
  ({
    images,
    tripName,
    onImageClick,
  }: {
    images: string[];
    tripName: string;
    onImageClick: (index: number) => void;
  }) => (
    <div className="w-full h-56 grid grid-cols-3 gap-2 mt-3 mb-2">
      {images.slice(0, 3).map((image, index) => (
        <motion.div
          key={index}
          className="relative h-full overflow-hidden rounded-lg group cursor-pointer"
          onClick={() => onImageClick(index)}
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          <LazyLoad
            src={formatImageUrl(image, "high")}
            lowResSrc={formatImageUrl(image, "medium")}
            alt={`${tripName} image ${index + 1}`}
            className="rounded-lg w-full h-full object-cover transition-transform duration-200"
            objectFit="cover"
          />
        </motion.div>
      ))}
    </div>
  )
);

ImageGrid.displayName = "ImageGrid";

const ExplorePost: React.FC<ExplorePostProps> = memo(
  ({ post, anonymousUser }) => {
    const [localLikeCount, setLocalLikeCount] = useState(
      post.socialStats.likes
    );
    const [localUserLiked, setLocalUserLiked] = useState(false);
    const [localShareCount, setLocalShareCount] = useState(
      post.socialStats.shares
    );
    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
      null
    );
    const [isOpen, setIsOpen] = useState(false);
    const { recordEngagement, copyTrip } = useTrip();
    const setRateLimit = useRateLimitStore((state) => state.setRateLimit);
    const navigate = useNavigate();

    const creatorName = useMemo(() => {
      if (post.owner.firstName && post.owner.lastName) {
        return `${post.owner.firstName} ${post.owner.lastName}`;
      }
      return post.owner.username;
    }, [post.owner.firstName, post.owner.lastName, post.owner.username]);

    const handleLike = useCallback(async () => {
      const action = localUserLiked ? "unlike" : "like";
      setLocalUserLiked(!localUserLiked);
      setLocalLikeCount((prevCount) =>
        localUserLiked ? prevCount - 1 : prevCount + 1
      );

      try {
        await recordEngagement(post.id, action, {
          trip_id: post.id,
          owner_id: post.owner.id,
        });
      } catch (error) {
        // Revert on error
        setLocalUserLiked(localUserLiked);
        setLocalLikeCount(localLikeCount);
        console.error("Failed to update like:", error);
      }
    }, [
      localUserLiked,
      localLikeCount,
      post.id,
      post.owner.id,
      recordEngagement,
    ]);

    const handleCopy = useCallback(async () => {
      try {
        await copyTrip(post.id);
      } catch (error) {
        console.error("Failed to copy trip:", error);
      }
    }, [post.id, copyTrip]);

    const handleTripClick = useCallback(() => {
      navigate(`/trip-builder/${post.id}`);
    }, [navigate, post.id]);

    const handleAccountClick = useCallback(() => {
      if (anonymousUser) {
        setRateLimit("Create an account to view this profile");
      } else {
        navigate(`/account/${post.owner.id}`);
      }
    }, [navigate, post.owner.id, anonymousUser, setRateLimit]);

    const handleImageClick = useCallback((index: number) => {
      setSelectedImageIndex(index);
    }, []);

    const closeImageModal = useCallback(() => {
      setSelectedImageIndex(null);
    }, []);

    return (
      <div className="border rounded-lg px-3 py-2 bg-white h-full w-full">
        {/* Header Section */}
        <div className="flex flex-row items-center justify-between mb-0.5">
          <button
            className="flex flex-row space-x-3 items-center hover:text-blue-600 transition-colors duration-200"
            onClick={handleTripClick}
          >
            <span className="font-medium text-base 2xl:text-lg ml-0.5">
              {post.name}
            </span>
          </button>
          <AddButton tripName={post.name} onAdd={handleCopy} />
        </div>

        {/* User Info Section */}
        <div className="flex flex-row space-x-1.5 items-center mb-2">
          <button
            onClick={handleAccountClick}
            className="flex flex-row space-x-2 items-center group"
          >
            <AvatarImage
              src={post.owner.profilePic}
              firstName={post.owner.firstName}
              lastName={post.owner.lastName}
              className="w-4 h-4 2xl:w-6 2xl:h-6 rounded-full"
            />
            <h4 className="text-xs 2xl:text-[12px] font-medium group-hover:text-indigo-600 text-neutral-500 transition-colors duration-200">
              {creatorName}
            </h4>
          </button>
          <div className="flex flex-row space-x-1 items-center">
            <p className="text-xs 2xl:text-[12px] text-neutral-500">•</p>
            <p className="text-xs 2xl:text-[12px] text-neutral-500 font-light">
              {formatCreatedAtAgo(post.createdAt)}
            </p>
          </div>
        </div>

        {/* Trip Details Section */}
        <div className="rounded-lg flex flex-col">
          <div className="flex flex-row flex-wrap gap-1.5">
            <span className="bg-neutral-100 text-neutral-700 text-[11.5px] 2xl:text-[12px] font-medium px-2.5 py-1 rounded-lg">
              {post.locations.map((location) => location.name).join(" • ")}
            </span>
            <span className="bg-neutral-100 text-neutral-700 text-[11.5px] 2xl:text-[12px] font-medium px-2.5 py-1 rounded-lg">
              {post.locations.length} days
            </span>
            {/* <span className="bg-neutral-100 text-neutral-700 text-[11.5px] 2xl:text-[12px] font-medium px-2.5 py-1 rounded-lg">
              Est. ${post.estimatedCost}
            </span> */}
          </div>

          <p className="mt-2 text-neutral-700 text-[13px] 2xl:text-[13.5px] leading-relaxed line-clamp-3">
            {post.description}
          </p>
        </div>

        {/* Images Grid Section */}
        {post.images.length > 0 && (
          <ImageGrid
            images={post.images}
            tripName={post.name}
            onImageClick={handleImageClick}
          />
        )}

        {/* Engagement Section */}
        <div className="flex items-center flex-row w-full justify-start ml-0.5">
          <EngagementButton onClick={handleLike} count={localLikeCount}>
            <LikeButton
              count={localLikeCount}
              iconClassName="fa-lg"
              userLiked={localUserLiked}
              isLiked={localUserLiked}
            />
          </EngagementButton>

          <ShareButton
            count={localShareCount}
            tripId={post.id}
            tripName={post.name}
            tripCreator={post.owner.username}
            iconClassName="fa-lg"
            isModalOpen={isOpen}
            setIsModalOpen={setIsOpen}
            trigger={
              <EngagementButton
                onClick={() => {}}
                count={localShareCount}
                className="ml-3"
              >
                <div className="w-6 h-8 flex items-center justify-center">
                  <PaperAirplaneIcon className="w-[19px] h-5 absolute transition-opacity duration-300 opacity-100 group-hover:opacity-0" />
                  <PaperAirplaneIconSolid className="w-[19px] h-5 text-blue-600 transition-opacity duration-300 opacity-0 group-hover:opacity-100" />
                </div>
              </EngagementButton>
            }
          />

          <EngagementButton
            onClick={() => {}}
            count={post.socialStats.views}
            className="ml-auto cursor-default pr-2"
          >
            <EyeIcon className="w-[19px] h-5" />
          </EngagementButton>
        </div>

        {/* Image Modal */}
        <AnimatePresence>
          {selectedImageIndex !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
              onClick={closeImageModal}
            >
              <img
                src={formatImageUrl(
                  post.images[selectedImageIndex] || "",
                  "high"
                )}
                alt={`${post.name} full size image`}
                className="max-h-[90vh] max-w-[90vw] object-contain"
                loading="eager"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

ExplorePost.displayName = "ExplorePost";

export default ExplorePost;
